import React, { useEffect, useState } from "react";
import { CheckCircle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RemittanceDetails = () => {
  const [search, setSearch] = useState("");
  const [remittance, setRemittance] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;

    const fetchRemittanceData = async () => {
      try {
        const [session, token] = document.cookie.split("=");

        if (!token) {
          console.error("Authorization token missing");
          return;
        }

        const response = await axios.get(
          `${REACT_APP_BACKEND_URL}/cod/remittanceTransactionData/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("Remittance Data:", response.data);

        if (response.data.success && response.data.data) {
          setRemittance(response.data.data);
        } else {
          console.error("API Error: Unsuccessful response", response.data);
        }
      } catch (error) {
        console.error("Error fetching remittance data:", error);
      }
    };

    fetchRemittanceData();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const navigateToBilling = () => {
    navigate("/dashboard/billing");
  };

  const handleTrackingByAwb = (awb) => {
    navigate(`/dashboard/order/tracking/${awb}`);
  };

  if (!remittance) {
    return <p className="text-center text-gray-500">Loading...</p>;
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Transaction Details */}
      <div className="flex items-center mb-4">
        <button onClick={navigateToBilling} className="text-gray-500 text-lg">
          &larr; Transaction Details
        </button>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
        <h2 className="text-xl font-bold text-blue-500">
          {remittance.remitanceId || "N/A"}
        </h2>
        <p className="text-gray-500 text-sm">
          Processed On: {formatDate(remittance.deliveryData)}
        </p>

        <span
          className={`px-3 py-1 rounded-full text-sm mt-2 inline-block ${
            remittance.status?.toLowerCase() === "pending"
              ? "bg-yellow-100 text-yellow-600"
              : "bg-green-100 text-green-600"
          }`}
        >
          {remittance.status?.toLowerCase() === "pending"
            ? "Pending"
            : "Completed"}
        </span>
      </div>

      {/* Remittance Details */}
      <div className="mt-6 bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Remittance Details
          </h3>
        </div>

        <div className="mt-4 bg-gray-50 p-6 rounded-lg flex flex-col items-center justify-center">
          <p className="text-xs ml-14 mt-4 pt-4">Remittance Amount</p>
          <div className="flex">
            <CheckCircle className="text-green-500 text-4xl mr-3" size={40} />
            <h3 className="text-2xl font-bold text-gray-900 mt-2">
            ₹{parseFloat(remittance.remitanceAmount || 0).toFixed(2)}

            </h3>
          </div>

          <div className="flex justify-between w-full text-sm text-gray-500 mt-4">
            <span>
              Total Orders:{" "}
              <strong className="text-gray-900">
                {remittance.totalOrder || 0}
              </strong>
            </span>
            <span>
              Remittance Date:{" "}
              <strong className="text-gray-900">
                {formatDate(remittance.deliveryData)}
              </strong>
            </span>
          </div>
        </div>
      </div>

      {/* AWB Numbers Section */}
      <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200 mt-6">
        <h3 className="text-sm font-bold text-[#2d054b]">AWB Numbers</h3>
        <input
          type="text"
          className="w-1/2 p-2 border border-gray-300 rounded-lg mt-3"
          placeholder="Search By AWB"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="mt-4 border-t pt-2">
          {remittance.orderDataInArray?.length > 0 ? (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b">
                  <th className="py-2 text-sm">Order ID</th>
                  <th className="py-2 text-sm">AWB Number</th>
                  <th className="py-2 text-sm">Courier Name</th>
                  <th className="py-2 text-sm">Order Total</th>
                </tr>
              </thead>
              <tbody>
                {remittance.orderDataInArray
                  .filter((item) =>
                    search
                      ? item.awb_number?.toString().includes(search)
                      : true
                  )
                  .map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2 text-xs text-blue-500">
                        {item.orderId || "N/A"}
                      </td>
                      <td className="py-2 text-xs">
                        <button
                          onClick={() =>
                            handleTrackingByAwb(item.awb_number)
                          }
                          className="text-blue-500 cursor-pointer"
                        >
                          {item.awb_number || "N/A"}
                        </button>
                      </td>
                      <td className="py-2 text-xs">
                        {item.provider || "N/A"}
                      </td>
                      <td className="py-2 text-xs">
                        ₹{item.paymentDetails?.amount || "0.00"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-sm text-center mt-4">
              No results found.
            </p>
          )}
        </div>
      </div>

      {/* Need Help Button */}
      <div className="text-center mt-6">
        <button className="bg-gray-100 px-4 py-2 rounded-lg text-gray-600">
          Need Help?
        </button>
      </div>
    </div>
  );
};

export default RemittanceDetails;
